<template>
  <div>
    <Breadcrumbs title="Packages" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div class="card-header pb-0">
              <div class="d-flex justify-content-between align-items-center">
                <h4 class="mb-0">
                  <i class="fas fa-box me-2"></i>
                  Package Management
                </h4>
                <div class="d-flex gap-2">
                  <button class="btn btn-primary" @click="openCreateModal" :disabled="!canCreate('packages')">
                    <i class="fas fa-plus me-1"></i>
                    Add Package
                  </button>
                </div>
              </div>
            </div>

            <div class="card-body">
              <!-- Filters and Search -->
              <div class="row mb-4">
                <div class="col-md-3">
                  <div class="input-group">
                    <span class="input-group-text">
                      <i class="fas fa-search"></i>
                    </span>
                    <input
                      v-model="searchQuery"
                      type="text"
                      class="form-control"
                      placeholder="Search packages..."
                      @input="handleSearch"
                    />
                  </div>
                </div>
                <div class="col-md-2">
                  <select
                    v-model="filterPackageType"
                    class="form-select"
                    @change="handleFilter"
                  >
                    <option value="">All Types</option>
                    <option value="free">Free</option>
                    <option value="starter">Starter</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <select
                    v-model="filterStatus"
                    class="form-select"
                    @change="handleFilter"
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <select
                    v-model="filterVisibility"
                    class="form-select"
                    @change="handleFilter"
                  >
                    <option value="">All Visibility</option>
                    <option value="true">Public</option>
                    <option value="false">Private</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <select
                    v-model="sortBy"
                    class="form-select"
                    @change="handleFilter"
                  >
                    <option value="name">Name</option>
                    <option value="slug">Slug</option>
                    <option value="packageType">Type</option>
                    <option value="sortOrder">Sort Order</option>
                    <option value="createdAt">Created Date</option>
                  </select>
                </div>
                <div class="col-md-1">
                  <button
                    class="btn btn-outline-secondary w-100"
                    @click="resetFilters"
                    title="Reset Filters"
                  >
                    <i class="fas fa-undo"></i>
                  </button>
                </div>
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <!-- Packages Table -->
              <div v-else-if="packages.length > 0" class="table-responsive table_style">
                <table class="table table-striped table-hover align-middle">
                  <thead class="table-dark">
                    <tr>
                      <th scope="col" style="width: 80px">Sr No</th>
                      <th scope="col" style="width: 120px">Slug</th>
                      <th scope="col" style="min-width: 200px">Name</th>
                      <th scope="col" style="min-width: 200px">Description</th>
                      <th scope="col" style="width: 120px">Type</th>
                      <th scope="col" style="width: 130px">Features</th>
                      <th scope="col" style="width: 130px">Prices</th>
                      <th scope="col" style="width: 80px">Public</th>
                      <th scope="col" style="width: 80px">Status</th>
                      <th scope="col" style="width: 180px">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="table-group-divider">
                    <tr
                      v-for="(packageItem, index) in packages"
                      :key="packageItem._id"
                    >
                      <td class="text-center">
                        <span class="badge bg-secondary">
                          {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                        </span>
                      </td>
                      <td>
                        <code class="package-slug">{{ packageItem.slug }}</code>
                      </td>
                      <td>
                        <div>
                          <strong>{{ packageItem.name }}</strong>
                          <div
                            v-if="packageItem.sortOrder !== undefined"
                            class="text-muted small"
                          >
                            Order: {{ packageItem.sortOrder }}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div class="description-cell">
                          <span
                            :title="packageItem.description"
                            class="text-muted"
                          >
                            {{ truncateText(packageItem.description, 50) }}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          :class="
                            getPackageTypeBadgeClass(packageItem.packageType)
                          "
                          class="badge"
                        >
                          {{ formatPackageType(packageItem.packageType) }}
                        </span>
                      </td>
                      <td class="text-center">
                        <div
                          v-if="
                            packageItem.features &&
                            packageItem.features.length > 0
                          "
                        >
                          <button
                            class="btn btn-sm btn-outline-info"
                            @click="showFeatures(packageItem.features)"
                            title="View Features"
                          >
                            <i class="fas fa-puzzle-piece"></i>
                            {{ packageItem.features.length }}
                          </button>
                        </div>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td class="text-center">
                        <div
                          v-if="
                            packageItem.prices && packageItem.prices.length > 0
                          "
                        >
                          <button
                            class="btn btn-sm btn-outline-success"
                            @click="showPrices(packageItem.prices)"
                            title="View Prices"
                          >
                            <i class="fas fa-dollar-sign"></i>
                            {{ packageItem.prices.length }}
                          </button>
                        </div>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td class="text-center">
                        <span
                          :class="
                            packageItem.isPublic
                              ? 'badge bg-success'
                              : 'badge bg-secondary'
                          "
                        >
                          {{ packageItem.isPublic ? "Public" : "Private" }}
                        </span>
                      </td>
                      <td class="text-center">
                        <div class="form-check form-switch">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :checked="packageItem.isActive"
                            @change="
                              handleToggleStatus(packageItem._id, $event)
                            "
                            :disabled="loading || !canUpdate('packages')"
                          />
                          <label class="form-check-label text-xs">
                            {{ packageItem.isActive ? "Active" : "Inactive" }}
                          </label>
                        </div>
                      </td>
                      <td>
                          <div class="d-flex gap-2" role="group">
                            <button
                              class="btn btn-sm btn-outline-primary"
                              @click="viewPackage(packageItem)"
                              title="View"
                              :disabled="!canView('packages')"
                            >
                              <i class="fas fa-eye"></i>
                            </button>
                            <button
                              class="btn btn-sm btn-outline-success"
                              @click="editPackage(packageItem)"
                              title="Edit"
                              :disabled="!canUpdate('packages')"
                            >
                              <i class="fas fa-edit"></i>
                            </button>
                            <button
                              class="btn btn-sm btn-outline-warning"
                              @click="duplicatePackage(packageItem)"
                              title="Duplicate"
                              :disabled="!canCreate('packages')"
                            >
                              <i class="fas fa-copy"></i>
                            </button>
                            <button
                              class="btn btn-sm btn-outline-danger"
                              @click="deletePackage(packageItem._id)"
                              title="Delete"
                              :disabled="!canDelete('packages')"
                            >
                              <i class="fas fa-trash"></i>
                            </button>
                          </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Empty State -->
              <div v-else class="text-center py-5">
                <i class="fas fa-box fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No packages found</h5>
                <p class="text-muted">
                  Create your first package to get started.
                </p>
                <button class="btn btn-primary" @click="openCreateModal" :disabled="!canCreate('packages')">
                  <i class="fas fa-plus me-1"></i>
                  Add Package
                </button>
              </div>

              <!-- Pagination -->
              <div
                v-if="totalItems > 0"
                class="d-flex justify-content-between align-items-center mt-4 pt-3 border-top"
              >
                <div class="text-muted">
                  <small>
                    Showing {{ startItem }} to {{ endItem }} of
                    {{ totalItems }} packages
                  </small>
                </div>
                <nav v-if="totalPages > 1" aria-label="Packages pagination">
                  <ul class="pagination pagination-sm mb-0">
                    <li
                      class="page-item"
                      :class="{ disabled: currentPage <= 1 }"
                    >
                      <button
                        class="page-link"
                        @click="changePage(currentPage - 1)"
                        :disabled="currentPage <= 1"
                        aria-label="Previous page"
                      >
                        <i class="fas fa-chevron-left"></i>
                        <span class="ms-1 d-none d-sm-inline">Previous</span>
                      </button>
                    </li>
                    <li
                      v-for="page in visiblePages"
                      :key="page"
                      class="page-item"
                      :class="{ active: page === currentPage }"
                    >
                      <button
                        v-if="typeof page === 'number'"
                        class="page-link"
                        @click="changePage(page)"
                        :aria-label="`Go to page ${page}`"
                        :aria-current="
                          page === currentPage ? 'page' : undefined
                        "
                      >
                        {{ page }}
                      </button>
                      <span v-else class="page-link">{{ page }}</span>
                    </li>
                    <li
                      class="page-item"
                      :class="{ disabled: currentPage >= totalPages }"
                    >
                      <button
                        class="page-link"
                        @click="changePage(currentPage + 1)"
                        :disabled="currentPage >= totalPages"
                        aria-label="Next page"
                      >
                        <span class="me-1 d-none d-sm-inline">Next</span>
                        <i class="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <PackageModal
      v-if="showModal"
      :show="showModal"
      :package="selectedPackage"
      :is-edit="isEditMode"
      :categories="categories"
      :features="allFeatures"
      :currencies="currencies"
      @close="closeModal"
      @saved="handlePackageSaved"
    />

    <!-- View Modal -->
    <PackageViewModal
      v-if="showViewModal"
      :show="showViewModal"
      :packageData="selectedPackage"
      :categories="categories"
      :features="allFeatures"
      :currencies="currencies"
      @close="closeViewModal"
      @edit="handleEditFromView"
    />

    <!-- Features Modal -->
    <div
      v-if="showFeaturesModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-puzzle-piece me-2"></i>
              Package Features
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeFeaturesModal"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedFeatures && selectedFeatures.length > 0">
              <div class="table-responsive">
                <table class="table table-sm table-hover">
                  <thead class="table-light">
                    <tr>
                      <th>Feature</th>
                      <th class="text-center">Status</th>
                      <th>Limits</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="feature in selectedFeatures"
                      :key="feature.featureId"
                    >
                      <td>
                        <div>
                          <strong>{{
                            getFeatureName(feature.featureId)
                          }}</strong>
                          <br />
                          <small class="text-muted">{{
                            typeof feature.featureId === "object"
                              ? feature.featureId.key
                              : feature.featureId
                          }}</small>
                        </div>
                      </td>
                      <td class="text-center">
                        <span
                          :class="
                            feature.enabled
                              ? 'badge bg-success'
                              : 'badge bg-secondary'
                          "
                        >
                          {{ feature.enabled ? "Enabled" : "Disabled" }}
                        </span>
                      </td>
                      <td>
                        <div
                          v-if="
                            getFeatureLimits(feature) &&
                            Object.keys(getFeatureLimits(feature)).length > 0
                          "
                        >
                          <div
                            v-for="(value, key) in getFeatureLimits(feature)"
                            :key="String(key)"
                            class="small"
                          >
                            <code>{{ key }}</code
                            >: {{ formatLimitValue(value) }}
                          </div>
                        </div>
                        <span v-else class="text-muted">No limits</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="text-center py-3">
              <i class="fas fa-puzzle-piece fa-2x text-muted mb-2"></i>
              <p class="text-muted mb-0">No features configured</p>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeFeaturesModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Prices Modal -->
    <div
      v-if="showPricesModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-dollar-sign me-2"></i>
              Package Pricing
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closePricesModal"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedPrices && selectedPrices.length > 0">
              <div class="table-responsive">
                <table class="table table-sm table-hover">
                  <thead class="table-light">
                    <tr>
                      <th>Currency</th>
                      <th class="text-end">Amount</th>
                      <th>Interval</th>
                      <th>Model</th>
                      <th class="text-end">Setup Fee</th>
                      <th class="text-center">Trial Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(price, index) in selectedPrices" :key="index">
                      <td>
                        <span class="badge bg-info">
                          {{ getCurrencyName(price.currency) }}
                        </span>
                      </td>
                      <td class="text-end">
                        <strong>{{
                          formatAmount(price.amount, price.currency)
                        }}</strong>
                      </td>
                      <td>
                        <span class="badge bg-primary">
                          {{ formatInterval(price.interval) }}
                        </span>
                      </td>
                      <td>
                        <span class="badge bg-secondary">
                          {{ formatPriceModel(price.priceModel) }}
                        </span>
                      </td>
                      <td class="text-end">
                        {{
                          price.setupFee
                            ? formatAmount(price.setupFee, price.currency)
                            : "-"
                        }}
                      </td>
                      <td class="text-center">
                        {{ price.trialDays || "-" }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="text-center py-3">
              <i class="fas fa-dollar-sign fa-2x text-muted mb-2"></i>
              <p class="text-muted mb-0">No pricing configured</p>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closePricesModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, defineAsyncComponent } from "vue";
import { toast } from "vue3-toastify";
import apiService from "@/services/apiService";
import { usePermissions } from "@/composables/usePermissions";

const { canView, canCreate, canUpdate, canDelete } = usePermissions();

const PackageModal = defineAsyncComponent(
  () => import("@/components/theme/admin/packages/PackageModal.vue")
);

const PackageViewModal = defineAsyncComponent(
  () => import("@/components/theme/admin/packages/PackageViewModal.vue")
);

// State
const packages = ref<any[]>([]);
const categories = ref<any[]>([]);
const allFeatures = ref<any[]>([]);
const currencies = ref<any[]>([]);
const loading = ref(false);
const searchQuery = ref("");
const filterPackageType = ref("");
const filterStatus = ref("");
const filterVisibility = ref("");
const sortBy = ref("name");
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalItems = ref(0);

// Modal state
const showModal = ref(false);
const showViewModal = ref(false);
const showFeaturesModal = ref(false);
const showPricesModal = ref(false);
const selectedPackage = ref<any>(null);
const selectedFeatures = ref<any[]>([]);
const selectedPrices = ref<any[]>([]);
const isEditMode = ref(false);

// Computed
const totalPages = computed(() =>
  Math.ceil(totalItems.value / itemsPerPage.value)
);

const startItem = computed(
  () => (currentPage.value - 1) * itemsPerPage.value + 1
);

const endItem = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, totalItems.value)
);

const visiblePages = computed(() => {
  const delta = 2;
  const range = [];
  const rangeWithDots = [];

  if (totalPages.value <= 1) {
    return [1];
  }

  if (totalPages.value <= 7) {
    for (let i = 1; i <= totalPages.value; i++) {
      range.push(i);
    }
    return range;
  }

  for (
    let i = Math.max(2, currentPage.value - delta);
    i <= Math.min(totalPages.value - 1, currentPage.value + delta);
    i++
  ) {
    range.push(i);
  }

  if (currentPage.value - delta > 2) {
    rangeWithDots.push(1, "...");
  } else {
    rangeWithDots.push(1);
  }

  rangeWithDots.push(...range);

  if (currentPage.value + delta < totalPages.value - 1) {
    rangeWithDots.push("...", totalPages.value);
  } else if (totalPages.value > 1) {
    rangeWithDots.push(totalPages.value);
  }

  return rangeWithDots.filter(
    (page, index, array) => array.indexOf(page) === index
  );
});

// Methods
const fetchPackages = async () => {
  try {
    loading.value = true;
    const params: any = {
      page: currentPage.value,
      limit: itemsPerPage.value,
    };

    if (searchQuery.value) {
      params.search = searchQuery.value;
    }

    if (filterPackageType.value) {
      params.packageType = filterPackageType.value;
    }

    if (filterStatus.value !== "") {
      params.isActive = filterStatus.value;
    }

    if (filterVisibility.value !== "") {
      params.isPublic = filterVisibility.value;
    }

    const response = await apiService.getPackages(params);

    // Handle the standardized response structure
    if (response?.packages) {
      packages.value = response.packages;
      totalItems.value =
        response.pagination?.totalItems || response.packages.length;
    } else if (response?.data?.packages) {
      packages.value = response.data.packages;
      totalItems.value =
        response.data.pagination?.totalItems || response.data.packages.length;
    } else if (Array.isArray(response)) {
      packages.value = response;
      totalItems.value = response.length;
    } else {
      packages.value = [];
      totalItems.value = 0;
    }
  } catch (error: any) {
    console.error("Error fetching packages:", error);

    let errorMessage = "Failed to load packages";
    if (error.response?.status === 401 || error.response?.status === 403) {
      errorMessage = "You are not authorized to view packages";
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }

    toast.error(errorMessage);
    packages.value = [];
  } finally {
    loading.value = false;
  }
};

const fetchCategories = async () => {
  try {
    const response = await apiService.getPackageCategories({ limit: 100 });
    if (response.data && response.data.categories) {
      categories.value = response.data.categories;
    } else if (response.categories) {
      categories.value = response.categories;
    } else {
      categories.value = response.data || response || [];
    }
  } catch (error) {
    console.error("Error fetching categories:", error);
  }
};

const fetchFeatures = async () => {
  try {
    const response = await apiService.getFeatures({ limit: 1000 });
    if (response.data && response.data.features) {
      allFeatures.value = response.data.features;
    } else if (response.features) {
      allFeatures.value = response.features;
    } else {
      allFeatures.value = response.data || response || [];
    }
  } catch (error) {
    console.error("Error fetching features:", error);
  }
};

const fetchCurrencies = async () => {
  try {
    const response = await apiService.getCurrencies({ limit: 100 });
    if (response.data && response.data.currencies) {
      currencies.value = response.data.currencies;
    } else if (response.currencies) {
      currencies.value = response.currencies;
    } else {
      currencies.value = response.data || response || [];
    }
  } catch (error) {
    console.error("Error fetching currencies:", error);
  }
};

const handleSearch = () => {
  currentPage.value = 1;
  fetchPackages();
};

const handleFilter = () => {
  currentPage.value = 1;
  fetchPackages();
};

const resetFilters = () => {
  searchQuery.value = "";
  filterPackageType.value = "";
  filterStatus.value = "";
  filterVisibility.value = "";
  sortBy.value = "name";
  currentPage.value = 1;
  fetchPackages();
};

const changePage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    fetchPackages();
  }
};

const openCreateModal = () => {
  selectedPackage.value = null;
  isEditMode.value = false;
  showModal.value = true;
};

const editPackage = (packageItem: any) => {
  selectedPackage.value = { ...packageItem };
  isEditMode.value = true;
  showModal.value = true;
};

const viewPackage = (packageItem: any) => {
  selectedPackage.value = packageItem;
  showViewModal.value = true;
};

const duplicatePackage = async (packageItem: any) => {
  const slugSuffix = "-copy-" + Date.now();
  const duplicateData = {
    slug: packageItem.slug + slugSuffix,
    name: packageItem.name + " (Copy)",
  };

  try {
    await apiService.duplicatePackage(packageItem._id, duplicateData);
    await fetchPackages();
    toast.success("Package duplicated successfully");
  } catch (error: any) {
    console.error("Error duplicating package:", error);

    let errorMessage = "Failed to duplicate package";
    if (error.response?.status === 401 || error.response?.status === 403) {
      errorMessage = "You are not authorized to duplicate packages";
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }

    toast.error(errorMessage);
  }
};

const closeModal = () => {
  showModal.value = false;
  selectedPackage.value = null;
  isEditMode.value = false;
};

const closeViewModal = () => {
  showViewModal.value = false;
  selectedPackage.value = null;
};

const handleEditFromView = (packageItem: any) => {
  closeViewModal();
  editPackage(packageItem);
};

const handlePackageSaved = () => {
  fetchPackages();
  closeModal();
  toast.success(
    isEditMode.value
      ? "Package updated successfully"
      : "Package created successfully"
  );
};

const handleToggleStatus = (id: string, event: Event) => {
  const target = event.target as HTMLInputElement;
  toggleStatus(id, target.checked);
};

const toggleStatus = async (id: string, status: boolean) => {
  try {
    // Optimistic update
    const packageIndex = packages.value.findIndex((pkg) => pkg._id === id);
    if (packageIndex !== -1) {
      packages.value[packageIndex].isActive = status;
    }

    await apiService.togglePackageStatus(id);
    toast.success(
      `Package ${status ? "activated" : "deactivated"} successfully`
    );

    await fetchPackages();
  } catch (error: any) {
    console.error("Error toggling status:", error);

    let errorMessage = "Failed to update package status";
    if (error.response?.status === 401 || error.response?.status === 403) {
      errorMessage = "You are not authorized to perform this action";
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }

    toast.error(errorMessage);

    // Revert optimistic update
    const packageIndex = packages.value.findIndex((pkg) => pkg._id === id);
    if (packageIndex !== -1) {
      packages.value[packageIndex].isActive = !status;
    }
  }
};

const deletePackage = async (id: string) => {
  if (
    !confirm(
      "Are you sure you want to delete this package? This action cannot be undone."
    )
  ) {
    return;
  }

  try {
    await apiService.deletePackage(id);
    await fetchPackages();
    toast.success("Package deleted successfully");
  } catch (error: any) {
    console.error("Error deleting package:", error);

    let errorMessage = "Failed to delete package";
    if (error.response?.status === 401 || error.response?.status === 403) {
      errorMessage = "You are not authorized to delete packages";
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }

    toast.error(errorMessage);
  }
};

const showFeatures = (features: any[]) => {
  selectedFeatures.value = features;
  showFeaturesModal.value = true;
};

const closeFeaturesModal = () => {
  showFeaturesModal.value = false;
  selectedFeatures.value = [];
};

const showPrices = (prices: any[]) => {
  selectedPrices.value = prices;
  showPricesModal.value = true;
};

const closePricesModal = () => {
  showPricesModal.value = false;
  selectedPrices.value = [];
};

// Utility functions
const truncateText = (text: string, maxLength: number): string => {
  if (!text) return "-";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

const getPackageTypeBadgeClass = (packageType: string): string => {
  const typeClasses: Record<string, string> = {
    free: "bg-success",
    starter: "bg-info",
    basic: "bg-info",
    professional: "bg-primary",
    enterprise: "bg-success text-white",
    custom: "bg-warning text-dark",
  };
  return typeClasses[packageType] || "bg-secondary";
};

const formatPackageType = (packageType: string): string => {
  return packageType.charAt(0).toUpperCase() + packageType.slice(1);
};

const getFeatureName = (feature: any): string => {
  // Handle both populated feature object and feature ID string
  if (typeof feature === "object" && feature !== null) {
    return feature.name || "Unknown Feature";
  } else if (typeof feature === "string") {
    const foundFeature = allFeatures.value.find((f) => f._id === feature);
    return foundFeature?.name || "Unknown Feature";
  }
  return "Unknown Feature";
};

const getCurrencyName = (currency: any): string => {
  // Handle both populated currency object and currency ID string
  if (typeof currency === "object" && currency !== null) {
    return currency.code || "Unknown Currency";
  } else if (typeof currency === "string") {
    const foundCurrency = currencies.value.find((c) => c._id === currency);
    return foundCurrency?.code || "Unknown Currency";
  }
  return "Unknown Currency";
};

const formatAmount = (amount: number, currency: any): string => {
  let symbol = "$";
  let decimalPlaces = 2;

  // Handle both populated currency object and currency ID string
  if (typeof currency === "object" && currency !== null) {
    symbol = currency.symbol || "$";
    decimalPlaces = currency.decimalPlaces || 2;
  } else if (typeof currency === "string") {
    const foundCurrency = currencies.value.find((c) => c._id === currency);
    symbol = foundCurrency?.symbol || "$";
    decimalPlaces = foundCurrency?.decimalPlaces || 2;
  }

  // Handle the case where amount might already be in the correct format
  const displayAmount = amount < 100 ? amount : amount / 100;
  return `${symbol}${displayAmount.toFixed(decimalPlaces)}`;
};

const formatInterval = (interval: string): string => {
  const intervals: Record<string, string> = {
    day: "Daily",
    week: "Weekly",
    month: "Monthly",
    year: "Yearly",
  };
  return intervals[interval] || interval;
};

const formatPriceModel = (priceModel: string): string => {
  const models: Record<string, string> = {
    flat: "Flat Rate",
    tiered: "Tiered",
    usage: "Usage-based",
    graduated: "Graduated",
  };
  return models[priceModel] || priceModel;
};

const formatLimitValue = (value: any): string => {
  if (value === -1) return "Unlimited";
  if (typeof value === "number") return value.toLocaleString();
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const getFeatureLimits = (feature: any): Record<string, any> => {
  // Check if feature has limits directly (old format)
  if (feature.limits && typeof feature.limits === "object") {
    return feature.limits;
  }

  // Check if feature has populated featureId with limits (new format)
  if (
    feature.featureId &&
    typeof feature.featureId === "object" &&
    feature.featureId.limits
  ) {
    return feature.featureId.limits;
  }

  return {};
};

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchCategories(),
    fetchFeatures(),
    fetchCurrencies(),
    fetchPackages(),
  ]);
});
</script>

<style scoped>
.package-slug {
  background-color: #f8f9fa;
  color: #495057 !important;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-family: "Courier New", monospace;
}

.description-cell {
  max-width: 200px;
}

/* Enhanced table header styling */
.table thead.table-dark th {
  background-color: var(--bs-dark) !important;
  color: var(--bs-white) !important;
  border-color: var(--bs-dark) !important;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.875rem;
  letter-spacing: 0.025em;
}

.table th,
.table td {
  vertical-align: middle;
  padding: 0.75rem;
}

.table {
  margin-bottom: 0;
}

/* Enhanced pagination styling */
.pagination .page-link {
  color: #495057;
  background-color: #fff;
  border: 1px solid #dee2e6;
  padding: 0.5rem 0.75rem;
  margin-left: -1px;
  line-height: 1.25;
  border-radius: 0;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out;
}

.pagination .page-link:hover {
  z-index: 2;
  color: #0056b3;
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.pagination .page-item:first-child .page-link {
  margin-left: 0;
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
}

.pagination .page-item:last-child .page-link {
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

.pagination .page-item.active .page-link {
  z-index: 3;
  color: #fff;
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d;
  background-color: #fff;
  border-color: #dee2e6;
  cursor: not-allowed;
}

/* Table responsiveness improvements */
.table-responsive {
  border-radius: 0.375rem;
  overflow: hidden;
}

.btn-group .btn {
  border-radius: 0.25rem;
  margin-right: 2px;
}

.btn-group .btn:last-child {
  margin-right: 0;
}

.badge {
  font-size: 0.75rem;
}

.text-xs {
  font-size: 0.75rem;
}

/* Card styling improvements */
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  background-color: rgba(0, 0, 0, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}
.table_style table{
  min-width: 1600px;
}
.table_style{
  overflow-x:auto;
}
</style>
